import { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';

const TRANSACTION_TYPES = [
  { value: 'payment', label: 'Payment', methods: ['etransfer_request_money', 'etransfer_standard'] },
  { value: 'payout', label: 'Payout', methods: ['etransfer_send_money'] },
  { value: 'refund', label: 'Refund', methods: ['etransfer_send_money'] },
  { value: 'chargeback', label: 'Chargeback', methods: ['etransfer_request_money'] }
];

const METHOD_LABELS: Record<string, string> = {
  etransfer_request_money: 'eTransfer Request Money',
  etransfer_standard: 'eTransfer Standard',
  etransfer_send_money: 'eTransfer Send Money'
};

const STATUSES = [
  { value: 'approved', label: 'Approved' },
  { value: 'declined', label: 'Declined' },
  { value: 'returned', label: 'Returned' }
];

import { Bank, BankFee } from '../../types';

interface BankFeeFormProps {
  bank: Bank;
  onClose: () => void;
}

export function BankFeeForm({ bank, onClose }: BankFeeFormProps) {
  const [fees, setFees] = useState<BankFee[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState<string>(TRANSACTION_TYPES[0].value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchFees();
  }, [bank.id]);

  async function fetchFees() {
    try {
      const { data, error } = await supabase
        .from('bank_fees')
        .select('*')
        .eq('bank_id', bank.id);

      if (error) throw error;
      setFees(data || []);
    } catch (err) {
      console.error('Error fetching fees:', err);
      setError('Failed to load bank fees');
    } finally {
      setLoading(false);
    }
  }

  const handleFeeChange = (
    type: string,
    method: string,
    status: string,
    field: keyof BankFee,
    value: string
  ) => {
    setFees((currentFees) => {
      const newFees = [...currentFees];
      const feeIndex = newFees.findIndex(
        (f) => f.type === type && f.payment_method === method && f.status === status
      );

      if (feeIndex >= 0) {
        newFees[feeIndex] = {
          ...newFees[feeIndex],
          [field]: field === 'payment_method' || field === 'status' || field === 'type' ? value : (value === '' ? 0 : parseFloat(value)),
        };
      } else {
        newFees.push({
          id: crypto.randomUUID(),
          bank_id: bank.id,
          type: type as 'payment' | 'payout' | 'refund' | 'chargeback',
          payment_method: method as 'etransfer_request_money' | 'etransfer_standard' | 'etransfer_send_money',
          status: status as 'approved' | 'declined' | 'returned',
          per_transaction_fee: 0,
          rate_fee_percentage: 0,
          max_fee_cap: 0,
          [field]: field === 'payment_method' || field === 'status' || field === 'type' ? value : (value === '' ? 0 : parseFloat(value)),
        } as BankFee);
      }

      return newFees;
    });
  };

  const getFee = (type: string, method: string, status: string) =>
    fees.find((f) => f.type === type && f.payment_method === method && f.status === status) || {
      per_transaction_fee: 0,
      rate_fee_percentage: 0,
      max_fee_cap: 0,
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const { error: upsertError } = await supabase
        .from('bank_fees')
        .upsert(fees, { onConflict: 'bank_id,type,payment_method,status' });

      if (upsertError) throw upsertError;
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save fees');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white rounded-t-2xl border-b border-slate-100">
          <div className="p-6 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Provider Fee Structure</h2>
              <p className="mt-0.5 text-[13px] text-slate-500">{bank.name} ({bank.alias})</p>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-6 flex gap-1 overflow-x-auto">
            {TRANSACTION_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => setSelectedType(type.value)}
                className={`px-4 py-2.5 text-[13px] font-medium rounded-t-lg border-b-2 transition-colors whitespace-nowrap ${
                  selectedType === type.value
                    ? 'border-violet-500 text-violet-600 bg-violet-50/50'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6">
            {TRANSACTION_TYPES.map((type) => (
              <div key={type.value} className={selectedType === type.value ? 'block' : 'hidden'}>
                <div className="space-y-6">
                  {type.methods.map((method) => (
                    <div key={method} className="card">
                      <div className="px-5 py-3 border-b border-slate-100">
                        <h4 className="section-title">{METHOD_LABELS[method]}</h4>
                      </div>
                      <div className="p-5 space-y-4">
                        {STATUSES.map((status) => {
                          const fee = getFee(type.value, method, status.value);
                          return (
                            <div key={status.value} className="rounded-xl bg-slate-50 p-4">
                              <h5 className="text-[13px] font-medium text-slate-700 mb-3">{status.label}</h5>
                              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                <div>
                                  <label className="block text-[12px] font-medium text-slate-500 mb-1">Fixed Fee / Txn</label>
                                  <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                      <span className="text-slate-400 text-[13px]">$</span>
                                    </div>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={fee.per_transaction_fee}
                                      onChange={(e) => handleFeeChange(type.value, method, status.value, 'per_transaction_fee', e.target.value)}
                                      className="w-full pl-7"
                                      placeholder="0.00"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-[12px] font-medium text-slate-500 mb-1">Rate %</label>
                                  <div className="relative">
                                    <input
                                      type="number"
                                      step="0.001"
                                      min="0"
                                      max="100"
                                      value={fee.rate_fee_percentage}
                                      onChange={(e) => handleFeeChange(type.value, method, status.value, 'rate_fee_percentage', e.target.value)}
                                      className="w-full pr-8"
                                      placeholder="0.000"
                                    />
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                      <span className="text-slate-400 text-[13px]">%</span>
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-[12px] font-medium text-slate-500 mb-1">Max Cap</label>
                                  <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                      <span className="text-slate-400 text-[13px]">$</span>
                                    </div>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={fee.max_fee_cap}
                                      onChange={(e) => handleFeeChange(type.value, method, status.value, 'max_fee_cap', e.target.value)}
                                      className="w-full pl-7"
                                      placeholder="0.00"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {error && (
              <div className="mt-4 p-3 rounded-xl bg-red-50 border border-red-100">
                <p className="text-[13px] text-red-600">{error}</p>
              </div>
            )}

            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
